/**
 * Bridge between the chat composer (director agent) and the code editor store.
 *
 * In `code` mode the same director model the user picked in the DirectorSelector
 * is steered with a coding system prompt that asks it to emit each file as a
 * fenced code block whose info string is the file path. `parseCodeFiles` pulls
 * those blocks back out so the composer can write them into `useCodeProject`,
 * making generated code show up in the editor + WASM preview.
 */
import type { ProjectFile } from './types';

export interface ParsedCodeFile {
  path: string;
  content: string;
}

/** System prompt that constrains the director to file-emitting output. */
export function codingSystemPrompt(existingFiles: ProjectFile[]): string {
  const manifest = existingFiles.length
    ? existingFiles.map(f => `- ${f.name}`).join('\n')
    : '(empty project)';
  return `You are a senior software engineer working inside an in-browser code editor with a live WASM preview (esbuild). The user describes what they want; you write the code.

RULES:
- For every file you create or change, output the COMPLETE file as a fenced code block whose opening fence info string is the file's full path relative to the project root. Example:
\`\`\`src/App.tsx
export default function App() { return <h1>Hi</h1>; }
\`\`\`
- One fenced block per file. Always output the entire file, never a diff or partial snippet.
- Prefer a runnable Vite + React project (entry: index.html + src/main.tsx + src/App.tsx) unless the user asks for something else.
- Keep a short plain-text explanation before the code blocks. Do not wrap the explanation in code fences.

CURRENT PROJECT FILES:
${manifest}`;
}

const FENCE_RE = /```([^\n`]*)\n([\s\S]*?)```/g;

/** True when a fence info-string token looks like a file path (has an extension). */
function looksLikePath(token: string): boolean {
  return /\.[A-Za-z0-9]+$/.test(token) && !/^https?:/i.test(token) && !token.includes('`');
}

/**
 * Extract `{ path, content }` for every fenced block whose info string carries a
 * file path. Language-only blocks (```ts) are ignored — they're display-only.
 */
export function parseCodeFiles(text: string): ParsedCodeFile[] {
  const out: ParsedCodeFile[] = [];
  let m: RegExpExecArray | null;
  FENCE_RE.lastIndex = 0;
  while ((m = FENCE_RE.exec(text)) !== null) {
    const info = (m[1] || '').trim();
    if (!info) continue;
    const pathToken = info.split(/\s+/).find(looksLikePath);
    if (!pathToken) continue;
    out.push({ path: pathToken.replace(/^\.?\//, ''), content: m[2].replace(/\n$/, '') });
  }
  return out;
}
